/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { type ReactNode } from 'react';

import { jsx } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { ConfluenceLogo } from '@atlaskit/logo';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, Stack, xcss } from '@atlaskit/primitives';
import { Hide, media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import type { SVGComponent } from '../../types';
import { Heading } from '../heading';
import { Text } from '../text';

type LayoutProps = {
	children: ReactNode;
	hideLogo?: boolean;
	screen: string;
	subtitle?: ReactNode;
	subtitleSize?: 'small' | 'medium' | 'large';
	title: string;
};

const styles = xcss({ width: '100%' });
const dynamicComponentEdgeToEdgeStyles = xcss({ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' });
const imageContainerStyles = xcss({
	display: 'none',

	[media.above.sm]: {
		overflow: 'hidden',
		display: 'block',
	},
});

const formStyles = xcss({
	padding: 'space.600',
});

const gridStyles = xcss({
	gridTemplateColumns: '1fr',
	height: '100%',
	[media.above.sm]: {
		gridTemplateColumns: '1fr 1fr',
	},
});

const leftSectionStyles = xcss({
	minHeight: '0px',
	overflowY: 'auto',
});

const updatedStyles = cssMap({
	headingContainer: {
		paddingBottom: token('space.100'),
	},
});

export const SplitScreen = ({
	applyUpdatedLayout = false,
	title,
	subtitle,
	subtitleSize,
	screen,
	imageComponent: Image,
	dynamicComponent,
	isDynamicComponentEdgeToEdge = false,
	imageAlt,
	footer,
	children,
	hideLogo = false,
}: {
	applyUpdatedLayout?: boolean;
	children: ReactNode;
	dynamicComponent?: ReactNode;
	footer: ReactNode;
	hideLogo?: boolean;
	imageAlt: string;
	imageComponent?: SVGComponent;
	isDynamicComponentEdgeToEdge?: boolean;
	screen: string;
	subtitle?: ReactNode;
	subtitleSize?: 'small' | 'medium' | 'large';
	title: string;
}): JSX.Element => {
	const layoutProps = { title, subtitle, subtitleSize, screen, children, hideLogo };
	const shouldApplyAriaHidden = FeatureGates.getExperimentValue(
		'cc_activation_a11y_fixes',
		'isEnabled',
		false,
	);

	return (
		<Grid xcss={gridStyles}>
			<Stack
				grow="fill"
				spread="space-between"
				space="space.400"
				xcss={[formStyles, leftSectionStyles]}
			>
				{applyUpdatedLayout ? <UpdatedLayout {...layoutProps} /> : <OldLayout {...layoutProps} />}
				{footer}
			</Stack>
			<Hide below="sm" xcss={imageContainerStyles}>
				{Image && (
					<Image
						viewBox="0 0 565 665"
						title={imageAlt}
						aria-label={imageAlt}
						preserveAspectRatio="xMinYMid slice"
						width="100%"
						height="100%"
						{...(!imageAlt && { 'aria-hidden': true, role: 'presentation' })}
					/>
				)}
				{!!dynamicComponent &&
					!Image &&
					(isDynamicComponentEdgeToEdge ? (
						<Box
							testId="split-screen-dynamic-component-wrapper"
							xcss={dynamicComponentEdgeToEdgeStyles}
						>
							{dynamicComponent}
						</Box>
					) : shouldApplyAriaHidden ? (
						<Box
							aria-hidden="true"
							testId="split-screen-dynamic-component-wrapper"
							xcss={dynamicComponentEdgeToEdgeStyles}
						>
							{dynamicComponent}
						</Box>
					) : (
						dynamicComponent
					))}
			</Hide>
		</Grid>
	);
};

const OldLayout = ({ title, subtitle, subtitleSize = 'medium', screen, children, hideLogo }: LayoutProps) => (
	<>
		<Stack alignInline="start" space="space.400">
			{!hideLogo && <ConfluenceLogo appearance="brand" />}
			<Stack alignInline="start" space={hideLogo ? 'space.200' : 'space.100'}>
				<Heading size="xlarge" testId={`${screen}-heading`} id={`${screen}-heading`}>
					{title}
				</Heading>
				{subtitle && <Text size={subtitleSize} color="color.text.subtle">{subtitle}</Text>}
			</Stack>
			<Box xcss={styles}>{children}</Box>
		</Stack>
	</>
);

const UpdatedLayout = ({ title, subtitle, subtitleSize = 'medium', screen, children, hideLogo }: LayoutProps) => (
	<>
		<Stack alignInline="start" space="space.400">
			{!hideLogo && <ConfluenceLogo appearance="brand" shouldUseNewLogoDesign />}
			<Stack alignInline="start" space="space.100">
				<Stack
					space={hideLogo ? 'space.200' : 'space.100'}
					xcss={cx(updatedStyles.headingContainer)}
				>
					<Heading size="xlarge" testId={`${screen}-heading`} id={`${screen}-heading`}>
						{title}
					</Heading>
					{subtitle && <Text size={subtitleSize} color="color.text.subtle">{subtitle}</Text>}
				</Stack>
				<Box xcss={styles}>{children}</Box>
			</Stack>
		</Stack>
	</>
);
